import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import {
  Button,
  Card,
  EmptyState,
  FilterBar,
  FilterSelect,
  InlineStats,
  Modal,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import { IconMail, IconRefresh } from '../../components/admin/icons';
import { formatDate } from '../../components/admin/labels';

const TOPIC_LABELS = {
  general: 'General',
  enterprise: 'Enterprise',
  courier: 'Courier provider',
  support: 'Support',
  billing: 'Billing'
};

const STATUS_TONES = {
  new: 'info',
  read: 'neutral',
  responded: 'success'
};

const ContactManagement = () => {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [active, setActive] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/contact-requests');
      setRequests(response.data || []);
    } catch (error) {
      toast('Could not load contact requests.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filtered = useMemo(
    () => (filterStatus ? requests.filter((r) => r.status === filterStatus) : requests),
    [requests, filterStatus]
  );

  const newCount = requests.filter((r) => r.status === 'new').length;

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const response = await api.patch(`/api/admin/contact-requests/${id}`, { status });
      setRequests((prev) => prev.map((r) => (r._id === id ? response.data : r)));
      setActive((prev) => (prev && prev._id === id ? response.data : prev));
    } catch (error) {
      toast('Could not update status.', { tone: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  // Opening a new message is itself the acknowledgement — no separate action
  // to mark it seen.
  const openRequest = (request) => {
    setActive(request);
    if (request.status === 'new') {
      updateStatus(request._id, 'read');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        {/* The three figures that were stat tiles across the top are the same
            three numbers, on one line — they were never worth a screen. */}
        <FilterBar
          actions={
            <>
              <InlineStats
                items={[
                  { label: 'Messages', value: loading ? '—' : requests.length },
                  { label: 'New', value: loading ? '—' : newCount },
                  {
                    label: 'Responded',
                    value: loading ? '—' : requests.filter((r) => r.status === 'responded').length
                  }
                ]}
              />
              <Button variant="secondary" size="sm" onClick={fetchRequests} loading={loading}>
                <IconRefresh size={15} />
                Refresh
              </Button>
            </>
          }
        >
          <FilterSelect
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="responded">Responded</option>
          </FilterSelect>
        </FilterBar>

        {loading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={IconMail}
            title={filterStatus ? `No ${filterStatus} messages` : 'No contact requests yet'}
            description="Messages appear here as soon as someone submits the contact form."
            action={
              filterStatus ? (
                <Button variant="secondary" size="sm" onClick={() => setFilterStatus('')}>
                  Show all statuses
                </Button>
              ) : null
            }
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Company</Th>
                <Th>Topic</Th>
                <Th>Status</Th>
                <Th>Date</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((request) => (
                <Tr
                  key={request._id}
                  className="cursor-pointer hover:bg-ink-50"
                  onClick={() => openRequest(request)}
                >
                  <Td className="font-600 text-ink-900">{request.name}</Td>
                  <Td className="text-brand-700">{request.email}</Td>
                  <Td>{request.company || <span className="text-ink-500">—</span>}</Td>
                  <Td>{TOPIC_LABELS[request.topic] || request.topic}</Td>
                  <Td>
                    <StatusPill tone={STATUS_TONES[request.status] || 'neutral'}>
                      {request.status}
                    </StatusPill>
                  </Td>
                  <Td className="whitespace-nowrap text-ink-500">
                    {formatDate(request.createdAt)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <Modal
        open={!!active}
        onClose={() => setActive(null)}
        title={active?.name}
        description={active?.email}
        footer={
          active && (
            <>
              <Button variant="secondary" onClick={() => setActive(null)}>
                Close
              </Button>
              <Button
                variant="primary"
                loading={updatingId === active._id}
                disabled={active.status === 'responded'}
                onClick={() => updateStatus(active._id, 'responded')}
              >
                Mark as responded
              </Button>
            </>
          )
        }
      >
        {active && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill tone={STATUS_TONES[active.status] || 'neutral'}>
                {active.status}
              </StatusPill>
              <StatusPill tone="neutral">{TOPIC_LABELS[active.topic] || active.topic}</StatusPill>
              {active.company && <StatusPill tone="neutral">{active.company}</StatusPill>}
            </div>
            <p className="text-xs text-ink-500">
              Submitted {formatDate(active.createdAt)} · reply directly to{' '}
              <a href={`mailto:${active.email}`} className="font-600 text-brand-700 underline">
                {active.email}
              </a>
            </p>
            <p className="whitespace-pre-wrap rounded-lg border border-ink-200 bg-ink-50/60 p-4 text-[15px] leading-relaxed text-ink-800">
              {active.message}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ContactManagement;
